// Liquid AC v2 canonical contract (ADR-0109 / EDG-1272).
// These value sets back the Postgres enums and are the single source of truth
// shared by the warehouse sync, the legacy->canonical mapper, and dbt accepted_values.

/**
 * Canonical liquid-AC payment patterns. `null` on the column means the item is
 * not liquid-AC-adjustable (there is no fake "no-adjust" pattern).
 */
export const LIQUID_AC_PATTERN_VALUES = [
  'P1_allin_TN',
  'P2_allin_SY',
  'P3_allin_CY',
  'P4_liquid',
] as const;
export type LiquidAcPattern = (typeof LIQUID_AC_PATTERN_VALUES)[number];

/**
 * Runtime adjustment status. Replaces the legacy `should_adjust` boolean as the
 * representation of runtime eligibility. Missing runtime data changes the status
 * (and/or warnings), never the configuration.
 */
export const ADJUSTMENT_STATUS_VALUES = [
  'applied',
  'not_adjustable',
  'unclassified',
  'missing_index_at_bid',
  'missing_index_today',
  'missing_liquid_ac_pct',
  'missing_conversion_factor',
  'missing_basis',
  'zero_actual_price',
  'no_matching_history',
  'unsupported_pattern',
] as const;
export type AdjustmentStatus = (typeof ADJUSTMENT_STATUS_VALUES)[number];

/** Non-fatal runtime warnings. A row can be `applied` and still carry warnings. */
export const ADJUSTMENT_WARNING_VALUES = [
  'proxy_index_used',
  'grade_fallback_used',
  'stale_index_used',
  'zone_fallback_used',
  'non_dot_inherited_config',
  'spec_rule_low_confidence',
] as const;
export type AdjustmentWarning = (typeof ADJUSTMENT_WARNING_VALUES)[number];

/** How a spec rule entered the table. */
export const LIQUID_AC_SPEC_RULE_SOURCE_VALUES = [
  'manual',
  'extracted',
  'imported',
] as const;
export type LiquidAcSpecRuleSource =
  (typeof LIQUID_AC_SPEC_RULE_SOURCE_VALUES)[number];

/**
 * Canonical PG binder grades (EDG-978): the Superpave grid plus the
 * polymer-modified "M" variants observed in DOT item descriptions and index
 * series. Single source for the rule-edit select, service validation, and any
 * future dbt accepted_values. Rule grades are constrained to this list;
 * item-detected grades and index series names stay free-form.
 */
export const PG_GRADE_VALUES = [
  'PG 46-28',
  'PG 46-34',
  'PG 52-28',
  'PG 52-34',
  'PG 58-22',
  'PG 58-28',
  'PG 58-34',
  'PG 64-16',
  'PG 64-22',
  'PG 64-28',
  'PG 64-34',
  'PG 67-22',
  'PG 70-16',
  'PG 70-22',
  'PG 70-22M',
  'PG 70-28',
  'PG 76-16',
  'PG 76-22',
  'PG 76-22M',
  'PG 76-28',
  'PG 82-16',
  'PG 82-22',
] as const;
export type PgGrade = (typeof PG_GRADE_VALUES)[number];
